import { useEffect, useMemo, useState } from "react";
import {
  ShieldAlert,
  RefreshCcw,
  Ban,
  X,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";

const RESULT_OPTIONS = [
  { value: "", label: "All results" },
  { value: "success", label: "Success (admin)" },
  { value: "wrong-password", label: "Wrong password" },
  { value: "unknown-email", label: "Unknown email" },
  { value: "rejected", label: "Rejected applicant" },
  { value: "blocked", label: "Blocked" },
];

const resultPillClass = (result, success) => {
  if (success) {
    return "border-emerald-700/40 text-emerald-700 bg-emerald-50/60";
  }
  if (result === "blocked") {
    return "border-red-600 text-white bg-red-600";
  }
  if (result === "rejected") {
    return "border-red-700/40 text-red-700 bg-red-50/60";
  }
  if (result === "wrong-password") {
    return "border-amber-600/50 text-amber-800 bg-amber-50/60";
  }
  // unknown-email + anything else
  return "border-[var(--ld-ink)]/30 text-[var(--ld-ink-muted)] bg-transparent";
};

const ResultIcon = ({ result, success }) => {
  if (success) return <CheckCircle2 size={12} strokeWidth={1.7} />;
  if (result === "blocked") return <Ban size={12} strokeWidth={1.7} />;
  if (result === "wrong-password" || result === "rejected")
    return <AlertTriangle size={12} strokeWidth={1.7} />;
  return <HelpCircle size={12} strokeWidth={1.7} />;
};

const trimUA = (ua) => {
  if (!ua) return "—";
  // Strip noisy version strings, keep the readable token.
  const m = ua.match(
    /(Edg|Chrome|Firefox|Safari|OPR|SamsungBrowser|FxiOS|CriOS)\/[\d.]+/
  );
  if (m) return m[0];
  return ua.length > 40 ? ua.slice(0, 38) + "…" : ua;
};

const SigninActivityPanel = () => {
  const [events, setEvents] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ email: "", ip: "", result: "" });
  const [actingId, setActingId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: evts }, { data: blks }] = await Promise.all([
        api.get("/admin/signin-activity", {
          params: {
            limit: 250,
            email: filter.email || undefined,
            ip: filter.ip || undefined,
            result: filter.result || undefined,
          },
        }),
        api.get("/admin/signin-blocks"),
      ]);
      setEvents(evts || []);
      setBlocks(blks || []);
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't load sign-in activity", {
        duration: 2800,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // Mount-only initial load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blockedKeys = useMemo(() => {
    const out = { ip: new Set(), email: new Set() };
    blocks.forEach((b) => out[b.kind]?.add(b.value));
    return out;
  }, [blocks]);

  const addBlock = async (kind, value) => {
    if (!value) return;
    if (
      !window.confirm(
        `Block this ${kind} from signing in?\n\n${value}\n\nYou can lift the block any time from this page.`
      )
    ) return;
    setActingId(`add-${kind}-${value}`);
    try {
      const { data } = await api.post("/admin/signin-blocks", { kind, value });
      setBlocks((rows) => {
        const without = rows.filter(
          (r) => !(r.kind === data.kind && r.value === data.value)
        );
        return [data, ...without];
      });
      toast(`${kind === "ip" ? "IP" : "Email"} blocked`, { duration: 1800 });
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not add block", {
        duration: 2800,
      });
    } finally {
      setActingId(null);
    }
  };

  const removeBlock = async (block) => {
    if (
      !window.confirm(
        `Lift the block on ${block.kind} ${block.value}? They will be able to sign in again immediately.`
      )
    ) return;
    setActingId(`rm-${block.id}`);
    try {
      await api.delete(`/admin/signin-blocks/${block.id}`);
      setBlocks((rows) => rows.filter((r) => r.id !== block.id));
      toast("Block lifted", { duration: 1800 });
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not lift block", {
        duration: 2800,
      });
    } finally {
      setActingId(null);
    }
  };

  // Top metrics
  const counts = useMemo(() => {
    const last24h = Date.now() - 24 * 3600 * 1000;
    let total = 0;
    let failures = 0;
    let recent = 0;
    events.forEach((e) => {
      total += 1;
      if (!e.success) failures += 1;
      const t = e.at ? new Date(e.at).getTime() : 0;
      if (t >= last24h && !e.success) recent += 1;
    });
    return { total, failures, recent, blocks: blocks.length };
  }, [events, blocks]);

  return (
    <div data-testid="section-signin-activity">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            Security
          </span>
          <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            Sign-in activity
          </h2>
          <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-2xl">
            Every failed sign-in attempt + every admin login, kept for 14 days.
            Use the block buttons to deny an IP or email immediately.
          </p>
        </div>
        <button
          data-testid="signin-activity-refresh"
          onClick={reload}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <RefreshCcw size={14} strokeWidth={1.6} />
          Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "events", label: "Events (14 d)", value: counts.total, Icon: ShieldAlert },
          { k: "failures", label: "Failures", value: counts.failures, Icon: AlertTriangle },
          { k: "recent", label: "Failures · last 24 h", value: counts.recent, Icon: AlertTriangle },
          { k: "blocks", label: "Active blocks", value: counts.blocks, Icon: Ban },
        ].map(({ k, label, value, Icon }) => (
          <div
            key={k}
            data-testid={`signin-kpi-${k}`}
            className="border border-[var(--ld-border)] p-5"
          >
            <div className="flex items-center justify-between text-[var(--ld-gold-deep)]">
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-accent text-[10px]">{label}</span>
            </div>
            <div className="mt-3 font-serif-display text-3xl text-[var(--ld-ink)]">
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Active blocks */}
      {blocks.length > 0 && (
        <div
          className="mt-8 border border-red-500/40 bg-red-500/[0.04]"
          data-testid="signin-blocks-panel"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-red-500/20">
            <span className="font-accent text-[10px] tracking-[0.3em] uppercase text-red-700">
              Active blocks · {blocks.length}
            </span>
          </div>
          <ul className="divide-y divide-red-500/15">
            {blocks.map((b) => (
              <li
                key={b.id}
                data-testid={`signin-block-row-${b.id}`}
                className="flex items-center justify-between gap-3 px-5 py-3"
              >
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-red-700">
                    <Ban size={12} strokeWidth={1.7} />
                    {b.kind}
                  </span>
                  <span className="ml-3 font-mono text-[13px] text-[var(--ld-ink)] break-all">
                    {b.value}
                  </span>
                  <div className="text-[11px] text-[var(--ld-ink-muted)] mt-0.5">
                    Blocked{" "}
                    {b.blocked_at
                      ? new Date(b.blocked_at).toLocaleString()
                      : "—"}
                    {b.blocked_by ? ` by ${b.blocked_by}` : ""}
                  </div>
                </div>
                <button
                  data-testid={`signin-block-lift-${b.id}`}
                  onClick={() => removeBlock(b)}
                  disabled={actingId === `rm-${b.id}`}
                  title="Lift this block"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors disabled:opacity-60 disabled:cursor-wait"
                >
                  <X size={11} strokeWidth={1.8} />
                  Lift block
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filters */}
      <div className="mt-8 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
            Email
          </span>
          <input
            type="text"
            data-testid="signin-filter-email"
            value={filter.email}
            onChange={(e) =>
              setFilter((s) => ({ ...s, email: e.target.value }))
            }
            placeholder="user@example.com"
            className="h-9 w-56 px-2 text-[12px] bg-transparent border border-[var(--ld-ink)]/25 focus:outline-none focus:border-[var(--ld-gold-deep)]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
            IP
          </span>
          <input
            type="text"
            data-testid="signin-filter-ip"
            value={filter.ip}
            onChange={(e) => setFilter((s) => ({ ...s, ip: e.target.value }))}
            placeholder="203.0.113.4"
            className="h-9 w-44 px-2 text-[12px] bg-transparent border border-[var(--ld-ink)]/25 focus:outline-none focus:border-[var(--ld-gold-deep)]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
            Result
          </span>
          <select
            data-testid="signin-filter-result"
            value={filter.result}
            onChange={(e) =>
              setFilter((s) => ({ ...s, result: e.target.value }))
            }
            className="h-9 w-48 px-2 text-[12px] bg-transparent border border-[var(--ld-ink)]/25 focus:outline-none focus:border-[var(--ld-gold-deep)]"
          >
            {RESULT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          data-testid="signin-filter-apply"
          onClick={reload}
          className="h-9 px-4 text-[10px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:bg-black hover:text-[#e9d59a] transition-colors"
        >
          Apply
        </button>
        {(filter.email || filter.ip || filter.result) && (
          <button
            type="button"
            data-testid="signin-filter-clear"
            onClick={() => {
              setFilter({ email: "", ip: "", result: "" });
              setTimeout(reload, 0);
            }}
            className="h-9 px-3 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/30 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="mt-4 border border-[var(--ld-border)]/80 overflow-x-auto">
        <table className="w-full text-sm" data-testid="signin-activity-table">
          <thead className="bg-[var(--ld-ink)]/[0.03] text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
            <tr>
              <th className="text-left font-accent font-normal px-4 py-3">When</th>
              <th className="text-left font-accent font-normal px-4 py-3">Email</th>
              <th className="text-left font-accent font-normal px-4 py-3">Role</th>
              <th className="text-left font-accent font-normal px-4 py-3">IP</th>
              <th className="text-left font-accent font-normal px-4 py-3">Agent</th>
              <th className="text-left font-accent font-normal px-4 py-3">Result</th>
              <th className="text-left font-accent font-normal px-4 py-3">Reason</th>
              <th className="text-right font-accent font-normal px-4 py-3">Block</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--ld-border)]/70">
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-[var(--ld-ink-muted)] text-sm">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && events.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-[var(--ld-ink-muted)] italic">
                  No sign-in activity yet.
                </td>
              </tr>
            )}
            {!loading &&
              events.map((e, idx) => {
                const ts = e.at ? new Date(e.at) : null;
                const emailBlocked = e.email && blockedKeys.email.has(e.email);
                const ipBlocked = e.ip && blockedKeys.ip.has(e.ip);
                return (
                  <tr
                    key={e.id || idx}
                    data-testid={`signin-event-row-${e.id || idx}`}
                    className="hover:bg-[var(--ld-ink)]/[0.02]"
                  >
                    <td className="px-4 py-3 text-[var(--ld-ink-muted)] whitespace-nowrap">
                      {ts ? ts.toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-[var(--ld-ink)] break-all max-w-[18ch]">
                      {e.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-[var(--ld-ink-muted)]">
                      {e.role || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px] text-[var(--ld-ink)] whitespace-nowrap">
                      {e.ip || "—"}
                    </td>
                    <td
                      className="px-4 py-3 text-[var(--ld-ink-muted)] text-[12px]"
                      title={e.user_agent || ""}
                    >
                      {trimUA(e.user_agent)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          "inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border " +
                          resultPillClass(e.result, e.success)
                        }
                      >
                        <ResultIcon result={e.result} success={e.success} />
                        {e.result || (e.success ? "success" : "failure")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--ld-ink-muted)] text-[12px] max-w-[28ch]">
                      {e.reason || "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          disabled={!e.ip || ipBlocked || actingId === `add-ip-${e.ip}`}
                          onClick={() => addBlock("ip", e.ip)}
                          data-testid={`signin-block-ip-${e.id || idx}`}
                          title={
                            !e.ip
                              ? "No IP recorded"
                              : ipBlocked
                              ? "IP already blocked"
                              : `Block ${e.ip}`
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 text-[var(--ld-ink)] hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Ban size={10} strokeWidth={1.8} />
                          IP
                        </button>
                        <button
                          type="button"
                          disabled={
                            !e.email ||
                            emailBlocked ||
                            actingId === `add-email-${e.email}`
                          }
                          onClick={() => addBlock("email", e.email)}
                          data-testid={`signin-block-email-${e.id || idx}`}
                          title={
                            !e.email
                              ? "No email recorded"
                              : emailBlocked
                              ? "Email already blocked"
                              : `Block ${e.email}`
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 text-[var(--ld-ink)] hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Ban size={10} strokeWidth={1.8} />
                          Email
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SigninActivityPanel;
